import { Alert, Box, Button, Dialog, DialogContent, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getAuth, logout } from "../allauth/lib/allauth";
import ApiClient from "../config/ApiClient";
import { useNativeWebViewInsets } from "../context/NativeWebViewInsetsContext";
import Logo from "../theme/Logo";
import { TrackedTextField } from "../theme/StyledFields";
import { nativeInsetSx } from "../utils/nativeWebViewInsets";
import { isUnder13 } from "../utils/coppa";

/**
 * Full-app age-assurance gate. Shown when the signed-in user's birthdate
 * indicates they are under 13 (COPPA). They can correct a mistyped date or log out.
 */
export default function CoppaGate() {
    const nativeInsets = useNativeWebViewInsets();
    const [birthdate, setBirthdate] = useState("");
    const [saving, setSaving] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const resp = await ApiClient.get<{ birthdate?: string | null }>("/api/account/profile");
            if (mounted && resp.success && resp.data?.birthdate) {
                setBirthdate(resp.data.birthdate);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    async function onCorrectBirthdate(e: React.FormEvent) {
        e.preventDefault();
        const value = birthdate.trim();
        if (!value) {
            setError("Please enter your birthdate.");
            return;
        }
        if (isUnder13(value)) {
            setError("If you are under 13, please talk to a parent or guardian first.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const resp = await ApiClient.patch("/api/account/profile", { birthdate: value }, {
                "Content-Type": "application/json",
            });
            if (!resp.success) {
                setError(resp.error || "Could not update birthdate. Please try again.");
                return;
            }
            await getAuth();
        } catch (err: any) {
            setError(err?.message || "Could not update birthdate. Please try again.");
        } finally {
            setSaving(false);
        }
    }

    async function onLogout() {
        setLoggingOut(true);
        setError(null);
        try {
            await logout();
        } catch (err: any) {
            setError(err?.message || "Could not log out. Please try again.");
            setLoggingOut(false);
        }
    }

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            disableEscapeKeyDown
            aria-labelledby="coppa-gate-title"
            data-testid="coppa-gate"
            PaperProps={{
                sx: nativeInsetSx(nativeInsets, "both"),
            }}
        >
            <DialogContent>
                <Stack spacing={3} alignItems="center" sx={{ textAlign: "center", py: 1 }}>
                    <Logo height={64} />
                    <Typography id="coppa-gate-title" variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                        Talk to a parent or guardian first
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        This app is for people 13 and older. Please ask a parent or guardian
                        to help you set things up before you continue.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
                            {error}
                        </Alert>
                    )}

                    <Box
                        component="form"
                        onSubmit={onCorrectBirthdate}
                        sx={{ width: "100%", textAlign: "left" }}
                    >
                        <TrackedTextField
                            fullWidth
                            required
                            label="Birthdate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={birthdate}
                            onChange={(e) => {
                                setBirthdate(e.target.value);
                                setError(null);
                            }}
                            helperText="If you selected this by mistake, update your birthdate here."
                            data-attr="coppa-gate_field-birthdate"
                        />
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={saving || loggingOut}
                                data-attr="coppa-gate_save-birthdate"
                            >
                                {saving ? "Saving..." : "Update birthdate"}
                            </Button>
                            <Button
                                variant="text"
                                onClick={onLogout}
                                disabled={loggingOut || saving}
                                data-attr="coppa-gate_logout"
                            >
                                {loggingOut ? "Logging out..." : "Log out"}
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
